import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Grid } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import * as moment from 'moment';
import 'moment/locale/pt-br';
import DateRangePicker from '~/components/DateRangePicker';
import Header from '~/components/Header';
import CustomHeader from '~/components/CustomHeader';
import NPSForm from '~/components/NPSForm';
import SalesTerms from '~/components/SalesTerms';
import { api, maisVendasApi } from '~/services/api';
import Snackbar from '~/util/SnackBar';
import {
    onlineChannels,
    physicalChannels,
} from '~/util/mapChannelsPhysicalAndOnline';
import { handleOrdersResponse } from '~/util/handleOrdersResponse';
import { shouldRemoveExternalProducts } from '~/util/functions';
import { ANIMALE, OFFPREMIUM } from '~/util/brands';

import {
    formatPrice,
    formatWithCent,
    formatCurrecyStringToNumberString,
    formatDecimalToPercentage,
} from '~/util/format';
import getDateRange from '~/util/getDateRange';
import getAssessmentDatesByDate from '~/util/getAssessmentDatesByDate';
import { signOut } from '../../store/modules/auth/actions';
import DesktopLayout from './DesktopLayout';
import * as Style from './styles';
import { changeShouldShowNPS } from '../../store/modules/user/actions';

function CotaPiloto({ location }) {
    const dispatch = useDispatch();

    const [aggregatedCapturedOrders, setAggregatedCapturedOrders] = useState(0);
    const [capturedOrdersIndicators, setCapturedOrdersIndicators] = useState(
        {}
    );
    const [totalOrdersIndicators, setTotalOrdersIndicators] = useState({});
    const [indicatorsText, setIndicatorsText] = useState({});
    const [quota, setQuota] = useState({});
    const [salesPercentage, setSalesPercentage] = useState('');
    const [salesNumber, setSalesNumber] = useState(0);
    const [balanceText, setBalanceText] = useState(0);
    const [panelLoaded, setPanelLoaded] = useState(false);
    const [chartData, setChartData] = useState([]);

    const [orders, setOrders] = useState([]);
    const [ordersLoaded, setOrdersLoaded] = useState(false);

    const [showNPSForm, setShowNPSForm] = useState(false);
    const [shouldLogout, setShouldLogout] = useState(false);

    const [brandFilter, setBrandFilter] = useState('myBrands');

    const userProfileFromStore = useSelector(state => state.user.profile);
    const [userProfile, setUserProfile] = useState(undefined);
    const [salesCodeFromParams, setSalesCodeFromParams] = useState('');

    const { initialDate, finalDate, isFaturado, period } = useSelector(
        state => state.status
    );
    const dateRange = getDateRange(initialDate, finalDate, 'DD/MM/YYYY');
    const [aggregatedOrdersByPeriod, setAggregatedOrdersByPeriod] = useState(
        []
    );

    // Guarda o total de vendas do mês para uso no modo 'day'
    const monthSalesRef = useRef(null);

    // Data limite antes da qual produtos externos Off Premium não tinham lógica implementada.
    const EXTERNAL_OFFPREMIUM_DATE_LIMIT = new Date('2024-09-27');

    useEffect(() => {
        if (period === 'custom' && !initialDate && !finalDate) {
            setAggregatedCapturedOrders(0);
            setBalanceText(0);
            setSalesNumber(0);
            setSalesPercentage('');
            setQuota({});
            setCapturedOrdersIndicators({});
            setTotalOrdersIndicators({});
            setChartData([]);
            setAggregatedOrdersByPeriod([]);
            setOrders([]);
            setPanelLoaded(true);
        }
    }, [period, initialDate, finalDate]);

    async function handleSubmit() {
        try {
            await api.post('/nps', {
                answer: '',
                comment: '',
                isSalesperson: userProfile.isSalesperson,
                login: userProfile.isSalesperson
                    ? userProfile.salesCode
                    : userProfile.login,
            });
            dispatch(changeShouldShowNPS(false));
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const salesCode = query.get('salesCode');
        setSalesCodeFromParams(salesCode);

        const MANAGER_PROFILE_IDS = [
            6,
            7,
            8,
            9,
            10,
            11,
            12,
            13,
            15,
            16,
            17,
            18,
        ];

        if (
            salesCode &&
            MANAGER_PROFILE_IDS.includes(userProfileFromStore.id_perfil)
        ) {
            const getUserData = async () => {
                const response = await maisVendasApi.post(
                    'salesperson/user-data',
                    {
                        salesCode: salesCode.toUpperCase(),
                    }
                );
                setUserProfile(response.data.user);
            };
            getUserData();
        } else {
            setUserProfile(userProfileFromStore);
        }
    }, [location.search, userProfileFromStore]);

    useEffect(() => {
        if (shouldLogout) {
            dispatch(signOut());
        }
    }, [dispatch, shouldLogout]);

    useEffect(() => {
        const isFarm = userProfile?.marcaFilial === 2;
        const beforeDateLimit = new Date() < new Date('2023-05-26');
        const shouldAnswerNPS = userProfile?.shouldAnswerNPS;

        if (shouldAnswerNPS) {
            if (!(beforeDateLimit && isFarm)) {
                setShowNPSForm(userProfile.shouldAnswerNPS);
            }
        }
    }, [userProfile]);

    const checkIfCanMakeRequests = useCallback(() => {
        return finalDate && userProfile?.users_sales_code[0].codigo_vendedor;
    }, [finalDate, userProfile]);

    const getBrandsParamsForRequests = useCallback(() => {
        const allBrandsIds = Array.from({ length: 16 }, (_, i) => i + 1);
        const myBrands = userProfileFromStore.marcaFilial;

        switch (brandFilter) {
            case 'allBrands':
                return [];
            case 'myBrands':
                return myBrands;
            case 'otherBrands':
                return allBrandsIds.filter(id => !myBrands.includes(id));
            default:
                return [];
        }
    }, [brandFilter, userProfileFromStore]);

    const buildCommonRequestParams = useCallback(
        (startDate, endDate) => {
            const query = new URLSearchParams(location.search);
            const salesCode = query.get('salesCode');

            const params = {
                startDate: moment(startDate).format('YYYY-MM-DD'),
                endDate: moment(endDate).format('YYYY-MM-DD'),
                salesCode: userProfile.users_sales_code.map(
                    sc => sc.codigo_vendedor
                ),
                brands: getBrandsParamsForRequests(),
                transaction: 'true',
                removeExternalProducts: shouldRemoveExternalProducts(
                    userProfileFromStore.marcaFilial
                ),
            };

            // Antes da data limite, usa offExternal='exclude' em vez de removeExternalProducts
            if (
                moment(startDate).format('YYYY-MM-DD') <=
                moment(EXTERNAL_OFFPREMIUM_DATE_LIMIT).format('YYYY-MM-DD')
            ) {
                delete params.removeExternalProducts;
                params.offExternal = 'exclude';
            }

            // Farm: exclui vendas LATAM
            if ([2].includes(userProfileFromStore.marcaFilial)) {
                params.farmLatam = 'exclude';
            }

            // Visualização por gerente: filtra pela filial
            if (salesCode) {
                params.filialCode =
                    userProfileFromStore.users_branches[0].codigo_filial;
            }

            return params;
        },
        [
            location.search,
            userProfile,
            getBrandsParamsForRequests,
            userProfileFromStore,
        ]
    );

    useEffect(() => {
        const indicators = {
            totalValue: 0,
            totalAverageValue: 0,
            totalOrderQuantity: 0,
            totalProductQuantity: 0,
            totalAverageQuantity: 0,
            onlineTotalValue: 0,
            onlineAverageValue: 0,
            onlineAverageQuantity: 0,
            onlineOrderQuantity: 0,
            onlineProductQuantity: 0,
            physicalTotalValue: 0,
            physicalAverageValue: 0,
            physicalAverageQuantity: 0,
            physicalOrderQuantity: 0,
            physicalProductQuantity: 0,
        };

        Object.entries(capturedOrdersIndicators).forEach(
            ([channel, values]) => {
                if (onlineChannels.includes(channel)) {
                    indicators.onlineTotalValue += values.value;
                    indicators.onlineOrderQuantity += values.serviceQuantity;
                    indicators.onlineProductQuantity += values.productQuantity;
                }
                if (physicalChannels.includes(channel)) {
                    indicators.physicalTotalValue += values.value;
                    indicators.physicalOrderQuantity += values.serviceQuantity;
                    indicators.physicalProductQuantity +=
                        values.productQuantity;
                }
            }
        );

        indicators.onlineAverageQuantity = (
            indicators.onlineProductQuantity / indicators.onlineOrderQuantity ||
            0
        ).toFixed(2);
        indicators.onlineAverageValue =
            indicators.onlineTotalValue / indicators.onlineOrderQuantity || 0;

        indicators.physicalAverageQuantity = (
            indicators.physicalProductQuantity /
                indicators.physicalOrderQuantity || 0
        ).toFixed(2);
        indicators.physicalAverageValue =
            indicators.physicalTotalValue / indicators.physicalOrderQuantity ||
            0;

        if (
            totalOrdersIndicators &&
            userProfile &&
            Object.keys(totalOrdersIndicators).length > 0
        ) {
            const brandIndicators =
                totalOrdersIndicators[userProfile.marcaFilial];

            if (brandIndicators) {
                indicators.totalAverageValue =
                    brandIndicators.value / brandIndicators.serviceQuantity;
                indicators.totalAverageQuantity = (
                    brandIndicators.productQuantity /
                    brandIndicators.serviceQuantity
                ).toFixed(2);
                indicators.totalValue = brandIndicators.value;
                indicators.totalOrderQuantity = brandIndicators.serviceQuantity;
                indicators.totalProductQuantity =
                    brandIndicators.productQuantity;
            }

            // Animale inclui vendas Off Premium nos totais gerais
            if (userProfile.marcaFilial === ANIMALE) {
                const offPremiumIndicators = totalOrdersIndicators[OFFPREMIUM];

                if (offPremiumIndicators) {
                    indicators.totalAverageValue =
                        (offPremiumIndicators.value + indicators.totalValue) /
                        (offPremiumIndicators.serviceQuantity +
                            indicators.totalOrderQuantity);
                    indicators.totalAverageQuantity = (
                        (offPremiumIndicators.productQuantity +
                            indicators.totalProductQuantity) /
                        (offPremiumIndicators.serviceQuantity +
                            indicators.totalOrderQuantity)
                    ).toFixed(2);
                    indicators.totalValue += offPremiumIndicators.value;
                    indicators.totalOrderQuantity +=
                        offPremiumIndicators.serviceQuantity;
                    indicators.totalProductQuantity +=
                        offPremiumIndicators.productQuantity;
                }
            }
        }

        const monetaryFields = [
            'onlineTotalValue',
            'physicalTotalValue',
            'totalValue',
            'totalAverageValue',
            'physicalAverageValue',
            'onlineAverageValue',
        ];

        Object.keys(indicators).forEach(key => {
            if (monetaryFields.includes(key)) {
                indicators[key] = formatWithCent(indicators[key]);
            }
        });

        setIndicatorsText(indicators);
    }, [
        capturedOrdersIndicators,
        isFaturado,
        totalOrdersIndicators,
        userProfile,
    ]);

    useEffect(() => {
        if (aggregatedCapturedOrders && aggregatedCapturedOrders[0] === '-') {
            return setSalesPercentage('0%');
        }
        const salesValue = formatCurrecyStringToNumberString(
            aggregatedCapturedOrders
        );
        const quotaValue = formatCurrecyStringToNumberString(quota.totalQuota);
        const percentage = formatDecimalToPercentage(
            Number(salesValue) / Number(quotaValue)
        );
        setSalesPercentage(percentage);
    }, [isFaturado, aggregatedCapturedOrders, quota.totalQuota]);

    useEffect(() => {
        if (!checkIfCanMakeRequests()) return;

        setPanelLoaded(false);

        const getSalespersonQuota = async () => {
            let quotaStartDate = moment(initialDate);
            let quotaEndDate = moment(finalDate);

            if (period === 'month') {
                const assessmentDates = getAssessmentDatesByDate(
                    moment(finalDate),
                    userProfileFromStore.marcaFilial
                );
                if (assessmentDates) {
                    quotaStartDate = assessmentDates.startDate;
                    quotaEndDate = assessmentDates.endDate;
                }
            }

            const response = await api.get('salesperson/quota', {
                params: {
                    startDate: quotaStartDate.format('YYYY-MM-DD'),
                    endDate: quotaEndDate.format('YYYY-MM-DD'),
                    salesCode: userProfile.users_sales_code[0].codigo_vendedor,
                    brand: userProfileFromStore.marcaFilial,
                },
            });

            const monthPeriod = response.data.periodQuotas?.find(
                p => p.period === 'month'
            );
            const quotaForDisplay =
                period === 'month' && monthPeriod?.quotaUntilEndDate != null
                    ? monthPeriod.quotaUntilEndDate
                    : response.data.totalQuota;

            const normalizedPeriodQuotas =
                period === 'month'
                    ? response.data.periodQuotas?.map(p =>
                          p.period === 'month'
                              ? { ...p, quota: response.data.totalQuota }
                              : p
                      )
                    : response.data.periodQuotas;

            setQuota({
                ...response.data,
                periodQuotas: normalizedPeriodQuotas,
                totalQuotaAsNumber: quotaForDisplay,
                totalQuota: formatPrice(quotaForDisplay),
            });
        };

        const getAggregatedCapturedOrders = async () => {
            try {
                const params = buildCommonRequestParams(initialDate, finalDate);
                params.fatured = 'false';

                const response = await api.get(
                    '/salesperson/aggregated-orders',
                    {
                        params,
                    }
                );

                const { data: responseData } = response.data;

                setChartData(
                    period === 'day'
                        ? Object.values(responseData.aggregatedByHour).map(
                              h => ({
                                  value: h.value,
                                  label: h.prettyHour,
                              })
                          )
                        : getDateRange(
                              initialDate,
                              finalDate,
                              'YYYY-MM-DD'
                          ).map(date => ({
                              value:
                                  responseData.aggregatedByDay[date.label]
                                      ?.value,
                              label: date.formattedLabel,
                          }))
                );

                setTotalOrdersIndicators(responseData.aggregatedByBrand);

                const totalValue =
                    responseData.total.value + responseData.returns.value;

                setAggregatedCapturedOrders(formatPrice(totalValue));
                setBalanceText(formatPrice(totalValue));
                setSalesNumber(Number(totalValue) * 100);
                setCapturedOrdersIndicators(responseData.aggregatedByChannel);
                setAggregatedOrdersByPeriod(response.data.periodSales);

                if (period === 'month') {
                    monthSalesRef.current = Number(totalValue) * 100;
                }

                if (response.data.msg) {
                    Snackbar.info(response.data.msg);
                }
            } catch {
                Snackbar.error(
                    'Erro retornando os indicadores de pedidos capturados'
                );
            }
        };

        const fetchMonthSales = async () => {
            const assessmentDates = getAssessmentDatesByDate(
                moment(finalDate),
                userProfileFromStore.marcaFilial
            );
            const startDate =
                assessmentDates?.startDate ??
                moment(finalDate).startOf('month');
            const endDate =
                assessmentDates?.endDate ?? moment(finalDate).endOf('month');

            const params = buildCommonRequestParams(startDate, endDate);
            params.fatured = 'false';

            const response = await api.get('/salesperson/aggregated-orders', {
                params,
            });
            const { data: responseData } = response.data;
            const totalValue =
                responseData.total.value + responseData.returns.value;

            monthSalesRef.current = Number(totalValue) * 100;
        };

        const ensureMonthSales = async () => {
            if (period === 'day' && !monthSalesRef.current) {
                await fetchMonthSales();
            }
        };

        Promise.all([
            ensureMonthSales().then(() => getAggregatedCapturedOrders()),
            getSalespersonQuota(),
        ])
            .catch(error => {
                if (error.response && error.response.status === 401) {
                    Snackbar.error('Token expirado. Faça login novamente');
                    setShouldLogout(true);
                }
            })
            .finally(() => setPanelLoaded(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        finalDate,
        checkIfCanMakeRequests,
        userProfile,
        isFaturado,
        getBrandsParamsForRequests,
    ]);

    useEffect(() => {
        setOrders([]);
        if (!checkIfCanMakeRequests()) return;

        setOrdersLoaded(false);

        const getSalespersonOrders = async () => {
            try {
                const params = buildCommonRequestParams(initialDate, finalDate);
                params.orderType = isFaturado ? 'invoiced' : 'captured';

                const response = await api.get('salesperson/orders', {
                    params,
                });
                const formattedOrderData = await handleOrdersResponse(
                    response.data.data,
                    false
                );
                setOrders(formattedOrderData);
            } catch {
                Snackbar.error('Erro retornando a lista de pedidos');
                setOrders([]);
            }
            setOrdersLoaded(true);
        };

        getSalespersonOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        finalDate,
        checkIfCanMakeRequests,
        userProfile,
        isFaturado,
        brandFilter,
    ]);

    return (
        <>
            <NPSForm
                open={showNPSForm}
                onClose={() => {
                    setShowNPSForm(false);
                    handleSubmit();
                }}
            />
            <SalesTerms />

            {location.search.includes('salesCode') ? (
                <CustomHeader
                    title={
                        userProfile &&
                        `${userProfile?.nome
                            .split(' ')[0]
                            .toLowerCase()} (${salesCodeFromParams})`
                    }
                />
            ) : (
                <Header />
            )}

            <DateRangePicker hidden={!panelLoaded} assessment />

            {!panelLoaded && (
                <Style.PanelLoadingContainer container spacing={1}>
                    <Grid container justifyContent="center" item xs={12}>
                        <Skeleton animation="wave" width="150px" />
                    </Grid>
                    <Grid container justifyContent="center" item xs={12}>
                        <Skeleton animation="wave" width="200px" />
                    </Grid>
                </Style.PanelLoadingContainer>
            )}

            <DesktopLayout
                balanceText={balanceText}
                indicatorsText={indicatorsText}
                panelLoaded={panelLoaded}
                aggregatedOrdersByPeriod={aggregatedOrdersByPeriod}
                chartData={chartData}
                dateRange={dateRange}
                salesNumber={salesNumber}
                monthlySalesNumber={
                    period === 'day'
                        ? monthSalesRef.current ?? salesNumber
                        : salesNumber
                }
                quota={quota}
                salesPercentage={salesPercentage}
                orders={orders}
                brandFilter={brandFilter}
                setBrandFilter={setBrandFilter}
                ordersLoaded={ordersLoaded}
            />
        </>
    );
}

export default CotaPiloto;
